// ------------------------------
// components/TopButtons.tsx
// Button row used at the very top of the wallet UI
// ------------------------------

import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { ActionIcon, Badge, Group, Indicator, Menu } from "@mantine/core";
import {
  IconHome,
  IconCoins,
  IconSend2,
  IconHistory,
  IconMessage,
  IconBolt,
  IconCoinBitcoinFilled,
  IconBrandTether,
  IconAlertTriangle,
  IconCreditCard,
  IconHourglass,
} from "@tabler/icons-react";

import { BobsWorkspace } from "../stores/BobsWorkspace";
import { BOB_PAGES } from "../helpers/constants";
import TopMenu from "./details/topMenu";

import {
  TOP_BUTTON_VARIANT,
  TOP_BUTTON_SIZE,
  TOP_BUTTON_RADIUS,
  getTopButtonStyle,
  getActiveButtonStyle,
  getTopIconSize,
  getTopGap,
} from "./details/buttons";

/*  Full row of top-navigation buttons */
const TopButtons = observer(
  ({ bobsWorkspace }: { bobsWorkspace: BobsWorkspace }) => {
    const { selectedBob } = bobsWorkspace;
    const [isMounted, setIsMounted] = useState(false);

    /*  Defer window access until client-side to avoid SSR issues */
    useEffect(() => {
      setIsMounted(true);
    }, []);

    const buttonStyle = getTopButtonStyle();
    const activeButtonStyle = getActiveButtonStyle();
    const iconSize = getTopIconSize();
    const gap = getTopGap();

    /*  Helper to decide active vs. normal style */
    const styleFor = (page: string) =>
      selectedBob.openPage === page ? activeButtonStyle : buttonStyle;

    if (!isMounted) return null; // avoid hydration mismatch during SSR

    // hold payments waiting for a decision in ANY vault: visible from every
    // page (zz-support-hold-invoices-fable.txt 10.2)
    const undecidedHoldCount =
      bobsWorkspace.undecidedHoldInvoicesAcrossBobs.length;

    return (
      <Group gap={gap} justify="flex-end" mt="md">
        <ActionIcon
          variant={TOP_BUTTON_VARIANT}
          size={TOP_BUTTON_SIZE}
          radius={TOP_BUTTON_RADIUS}
          aria-label="Home"
          onClick={() => selectedBob.setOpenPage(BOB_PAGES.HOME)}
          style={styleFor(BOB_PAGES.HOME)}
        >
          <IconHome size={iconSize} />
        </ActionIcon>

        <Menu shadow="md" width={200}>
          <Menu.Target>
            <Indicator
              color="red"
              label={undecidedHoldCount}
              disabled={undecidedHoldCount === 0}
              size={20}
            >
              <ActionIcon
                variant={TOP_BUTTON_VARIANT}
                size={TOP_BUTTON_SIZE}
                radius={TOP_BUTTON_RADIUS}
                aria-label="Receive Funds"
                style={
                  selectedBob.openPage === BOB_PAGES.RECEIVE ||
                  selectedBob.openPage === BOB_PAGES.RECEIVE_ONCHAIN ||
                  selectedBob.openPage === BOB_PAGES.HOLD_INVOICES
                    ? activeButtonStyle
                    : buttonStyle
                }
              >
                <IconCoins size={iconSize} />
              </ActionIcon>
            </Indicator>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconBolt size={iconSize} />}
              onClick={() => selectedBob.setOpenPage(BOB_PAGES.RECEIVE)}
            >
              Receive Lightning
            </Menu.Item>
            <Menu.Item
              leftSection={<IconCoinBitcoinFilled size={iconSize} />}
              onClick={() => selectedBob.setOpenPage(BOB_PAGES.RECEIVE_ONCHAIN)}
            >
              Receive On-Chain
            </Menu.Item>
            {bobsWorkspace.showHoldInvoicesMenu && (
              <Menu.Item
                leftSection={<IconHourglass size={iconSize} />}
                onClick={() => selectedBob.setOpenPage(BOB_PAGES.HOLD_INVOICES)}
                rightSection={
                  undecidedHoldCount > 0 ? (
                    <Badge color="red" size="sm" circle>
                      {undecidedHoldCount}
                    </Badge>
                  ) : null
                }
              >
                Hold Invoices
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>

        <Menu shadow="md" width={200}>
          <Menu.Target>
            <ActionIcon
              variant={TOP_BUTTON_VARIANT}
              size={TOP_BUTTON_SIZE}
              radius={TOP_BUTTON_RADIUS}
              aria-label="Send Funds"
              style={
                selectedBob.openPage === BOB_PAGES.SEND ||
                selectedBob.openPage === BOB_PAGES.SEND_ONCHAIN ||
                selectedBob.openPage === BOB_PAGES.SEND_USDT ||
                selectedBob.openPage === BOB_PAGES.GIFT_CARD
                  ? activeButtonStyle
                  : buttonStyle
              }
            >
              <IconSend2 size={iconSize} />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconBolt size={iconSize} />}
              onClick={() => selectedBob.setOpenPage(BOB_PAGES.SEND)}
            >
              Send Lightning
            </Menu.Item>
            <Menu.Item
              leftSection={<IconCoinBitcoinFilled size={iconSize} />}
              onClick={() => selectedBob.setOpenPage(BOB_PAGES.SEND_ONCHAIN)}
            >
              Send On-Chain
            </Menu.Item>
            {/* DISABLED: Send USDT (Solana) is turned off. The backend routes are
                commented out in config/routes.rb. Flip this to `true` and restore
                those routes to re-enable. */}
            {false && (
              <Menu.Item
                leftSection={<IconBrandTether size={iconSize} />}
                onClick={() => selectedBob.setOpenPage(BOB_PAGES.SEND_USDT)}
                rightSection={
                  <IconAlertTriangle
                    size={16}
                    color="yellow"
                    style={{ opacity: 0.7 }}
                  />
                }
              >
                Send USDT
              </Menu.Item>
            )}
            <Menu.Item
              leftSection={<IconCreditCard size={iconSize} />}
              onClick={() => {
                selectedBob.setGiftCardView("shop");
                selectedBob.setOpenPage(BOB_PAGES.GIFT_CARD);
              }}
            >
              Buy Gift Card
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>

        <ActionIcon
          variant={TOP_BUTTON_VARIANT}
          size={TOP_BUTTON_SIZE}
          radius={TOP_BUTTON_RADIUS}
          aria-label="Transactions"
          onClick={() => selectedBob.setOpenPage(BOB_PAGES.TRANSACTIONS_LIST)}
          style={styleFor(BOB_PAGES.TRANSACTIONS_LIST)}
        >
          <IconHistory size={iconSize} />
        </ActionIcon>

        <Indicator
          color="gray"
          label={bobsWorkspace.events.length}
          disabled={bobsWorkspace.events.length === 0}
          size={20}
        >
          <ActionIcon
            variant={TOP_BUTTON_VARIANT}
            size={TOP_BUTTON_SIZE}
            radius={TOP_BUTTON_RADIUS}
            aria-label="Messages"
            onClick={() =>
              bobsWorkspace.setEventsDrawerIsShown(
                !bobsWorkspace.eventsDrawerIsShown,
              )
            }
            style={buttonStyle}
          >
            <IconMessage size={iconSize} />
          </ActionIcon>
        </Indicator>

        {/*  Kebab menu / settings */}
        <TopMenu bobsWorkspace={bobsWorkspace} />
      </Group>
    );
  },
);

export default TopButtons;
